import React from "react";
import styled from "styled-components";
import SolitaireCore from "../SolitaireCore";
import DesktopIcon from "../DesktopIcon";
import PixelIcon from "../PixelIcon";
import { RecycleBinIcon } from "../RecycleBin";
import { TASKBAR_HEIGHT } from "../Taskbar";
import { newGame, applyMoveByIndex, legalMoves } from "../../engine/solitaire";

// The Layout Studio (`?layout`): a standalone bench for making window
// arrangements. No server, no boot, no bots — pick a target
// resolution, see a to-scale simulation of SAT at exactly that size,
// add dummy windows, drag them into a room, save the JSON, paste into
// Stage/layouts.js. Several saves at one resolution = variants (a
// session picks one at random).

// The resolutions worth caring about (desktop only — compact/mobile
// has its own fixed arrangement). Viewport sizes, i.e. AFTER browser
// chrome, which is why they're shorter than the marketing numbers:
// "1080p" screens hand the page ~950px. "Short" covers half-height
// windows and toolbar-heavy browsers; below/beside these, the loader
// stretches the nearest room (applyLayout), so the buckets can stay few.
const RESOLUTIONS = [
  { label: "Small laptop — 1280×660", w: 1280, h: 660 },
  { label: "MacBook — 1512×830", w: 1512, h: 830 },
  { label: "1080p — 1920×950", w: 1920, h: 950 },
  { label: "Big display — 2560×1300", w: 2560, h: 1300 },
  { label: "Short window — 1600×520", w: 1600, h: 520 },
];

const GAME_W = 620;
const GAME_H = 435;

// Same clamp PositionedWindow applies to live drags: title bar on
// screen and above the taskbar, a hand's width grabbable horizontally.
function clampCenter(x, y, W, H) {
  const GRAB_X = 60;
  let cx = W / 2 + x;
  let cy = H / 2 + y;
  cx = Math.max(GRAB_X - GAME_W / 2, Math.min(W - GRAB_X + GAME_W / 2, cx));
  cy = Math.max(GAME_H / 2, Math.min(H - TASKBAR_HEIGHT - 24 + GAME_H / 2, cy));
  return { x: cx - W / 2, y: cy - H / 2 };
}

// A plausibly mid-game board for a dummy window (same trick as the
// All Active Games' ?fakechaos knob).
function dummyState(i) {
  let st = newGame(1000000 + i);
  let seed = (i + 1) * 2654435761;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 2 ** 32;
  };
  const moves = 10 + Math.floor(rand() * 40);
  for (let m = 0; m < moves; m++) {
    const legal = legalMoves(st);
    if (legal.length === 0) break;
    st = applyMoveByIndex(st, Math.floor(rand() * legal.length));
  }
  return st;
}

const SELF_STATE = newGame(1137);

function LayoutStudio() {
  const [resIdx, setResIdx] = React.useState(0);
  // Per-resolution working state, kept across switches so several
  // rooms can be tweaked in one sitting. { self: {x,y},
  // dummies: [{id, x, y}], nextId }
  const [rooms, setRooms] = React.useState(() =>
    RESOLUTIONS.map(() => ({ self: { x: 0, y: 0 }, dummies: [], nextId: 0 })),
  );
  const [copied, setCopied] = React.useState(false);
  const [viewport, setViewport] = React.useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });
  React.useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const res = RESOLUTIONS[resIdx];
  const room = rooms[resIdx];
  // Boards are keyed by dummy id (not render index) so closing one
  // window never reshuffles the others' cards.
  const boardsRef = React.useRef(new Map());
  const dummyBoard = (id) => {
    if (!boardsRef.current.has(id)) boardsRef.current.set(id, dummyState(id));
    return boardsRef.current.get(id);
  };
  const TOOLBAR_H = 46;
  const scale = Math.min(
    (viewport.w - 24) / res.w,
    (viewport.h - TOOLBAR_H - 24) / res.h,
  );

  const patchRoom = (fn) =>
    setRooms((prev) => prev.map((r, i) => (i === resIdx ? fn(r) : r)));

  const addWindow = () =>
    patchRoom((r) => {
      const n = r.dummies.length;
      const spot = clampCenter(
        -140 + (n % 3) * 130,
        -90 + n * 52,
        res.w,
        res.h,
      );
      return {
        ...r,
        dummies: [...r.dummies, { id: r.nextId, ...spot }],
        nextId: r.nextId + 1,
      };
    });

  const removeWindow = (id) =>
    patchRoom((r) => ({
      ...r,
      dummies: r.dummies.filter((d) => d.id !== id),
    }));

  // Drag: plain pointer capture with scale compensation (screen px →
  // frame px). PositionedWindow isn't reused because its clamps are
  // viewport-based — here the FRAME is the world.
  const dragWindow = (e, get, set) => {
    if (e.button !== 0) return;
    if (e.target.closest("button, input")) return;
    if (!e.target.closest("[data-drag-handle]")) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const base = get();
    const onMove = (ev) => {
      const next = clampCenter(
        base.x + (ev.clientX - startX) / scale,
        base.y + (ev.clientY - startY) / scale,
        res.w,
        res.h,
      );
      set(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const save = async () => {
    const layout = {
      width: res.w,
      height: res.h,
      self: { x: Math.round(room.self.x), y: Math.round(room.self.y) },
      neighbors: room.dummies.map((d) => ({
        x: Math.round(d.x),
        y: Math.round(d.y),
      })),
    };
    const json = JSON.stringify(layout);
    // eslint-disable-next-line no-console
    console.log("[layout]", json);
    try {
      await navigator.clipboard.writeText(json + ",");
    } catch {
      /* console has it */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Studio>
      <Toolbar className="window">
        <ToolbarRow className="window-body">
          <select
            value={resIdx}
            onChange={(e) => setResIdx(Number(e.target.value))}
            aria-label="Target resolution"
          >
            {RESOLUTIONS.map((r, i) => (
              <option key={r.label} value={i}>
                {r.label}
              </option>
            ))}
          </select>
          <button type="button" onClick={addWindow}>
            Add window
          </button>
          <button type="button" onClick={save}>
            {copied ? "Copied!" : "Save layout JSON"}
          </button>
          <Meta>
            {room.dummies.length} neighbors · shown at {Math.round(scale * 100)}%
            · paste into Stage/layouts.js
          </Meta>
        </ToolbarRow>
      </Toolbar>
      <FrameArea>
        <Frame
          style={{
            "--w": res.w + "px",
            "--h": res.h + "px",
            "--scale": scale,
          }}
        >
          {/* The desktop furniture at its default anchors (matching
              Stage's DraggableIconSlot positions), inert — here so
              window placement can be judged against icon visibility. */}
          <IconLayer aria-hidden="true">
            <IconSlot style={{ "--top": "12px" }}>
              <DesktopIcon
                icon={<PixelIcon name="solitaire" size={32} />}
                label="Solitaire Alone Together"
              />
            </IconSlot>
            <IconSlot style={{ "--top": "116px" }}>
              <DesktopIcon
                icon={<PixelIcon name="folder" size={32} />}
                label="Past Games"
              />
            </IconSlot>
            <IconSlot style={{ "--top": "220px" }}>
              <DesktopIcon
                icon={<PixelIcon name="all-games" size={32} />}
                label="All Active Games"
              />
            </IconSlot>
            <IconSlot style={{ "--top": "324px" }}>
              <DesktopIcon
                icon={<PixelIcon name="text-file" size={32} />}
                label="stats.txt"
              />
            </IconSlot>
            <IconSlot
              style={{ "--top": "auto", "--bottom": TASKBAR_HEIGHT + 10 + "px" }}
            >
              <RecycleBinIcon />
            </IconSlot>
          </IconLayer>
          {room.dummies.map((d) => (
            <Slot
              key={d.id}
              style={{ "--x": d.x + "px", "--y": d.y + "px" }}
              onPointerDown={(e) =>
                dragWindow(
                  e,
                  () => d,
                  (next) =>
                    patchRoom((r) => ({
                      ...r,
                      dummies: r.dummies.map((w) =>
                        w.id === d.id ? { ...w, ...next } : w,
                      ),
                    })),
                )
              }
            >
              <SolitaireCore
                title={`Game #${1200 + d.id} — Solitaire Alone Together`}
                state={dummyBoard(d.id)}
                titleIcon="solitaire"
                interactive={false}
                inactive
                disabledControls={["minimize", "maximize"]}
                onControlClick={(control) => {
                  if (control === "close") removeWindow(d.id);
                }}
              />
            </Slot>
          ))}
          <Slot
            style={{ "--x": room.self.x + "px", "--y": room.self.y + "px" }}
            onPointerDown={(e) =>
              dragWindow(
                e,
                () => room.self,
                (next) => patchRoom((r) => ({ ...r, self: next })),
              )
            }
          >
            <SolitaireCore
              title="(You) Game #1137 — Solitaire Alone Together"
              state={SELF_STATE}
              interactive={false}
              disabledControls={["minimize", "maximize", "close"]}
            />
          </Slot>
          <FakeTaskbar>
            <button type="button" disabled>
              Start
            </button>
          </FakeTaskbar>
        </Frame>
      </FrameArea>
    </Studio>
  );
}

const Studio = styled.div`
  position: fixed;
  inset: 0;
  background: #1a1a2a;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  flex: none;
  margin: 4px;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
`;

const Meta = styled.span`
  font-size: 11px;
  color: #444;
`;

const FrameArea = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

// The simulated viewport: exactly the target resolution, scaled to
// fit. Absolutely centered — grid/flex centering goes start-aligned
// once the UNSCALED box overflows the area. Same felt-teal as the
// real desktop so the vibe reads true.
const Frame = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--w);
  height: var(--h);
  transform: translate(-50%, -50%) scale(var(--scale));
  background: #027f7f;
  overflow: hidden;
  outline: 2px solid #000;
`;

// A window in the frame: centered at (frame center + x, y), the same
// coordinate system PositionedWindow uses at runtime.
const Slot = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) translate(var(--x), var(--y));
`;

// Inert furniture: visible for judging occlusion, never interactive
// and never in the way of window drags.
const IconLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const IconSlot = styled.div`
  position: absolute;
  left: 10px;
  top: var(--top);
  bottom: var(--bottom, auto);
`;

const FakeTaskbar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${TASKBAR_HEIGHT}px;
  background: #c0c0c0;
  box-shadow: inset 0 1px #fff;
  display: flex;
  align-items: center;
  padding: 3px 4px;

  & button {
    min-width: 54px;
    font-weight: bold;
  }
`;

export default LayoutStudio;
